import { Guard } from '@mimir/common/async/async';
import type { RawAudioInfo, RawAudioFormat } from '@mimir/common/transcoding/rawFrame';
import { calculateRawAudioFrameBufferSize } from '@mimir/common/transcoding/rawFrame';
import type { Logger } from '@mimir/common/types/logger';
import { exceptionToString } from '@mimir/common/util/exception';
import type { Fraction } from '@mimir/common/util/fractionType';
import { approximateFractionValue, frac, multiplyFractionBy, multiplyFractions } from '@mimir/common/util/integerMath';
import { abortify } from '@mimir/common/util/abortify';
import type { AudioDecoder as AudioDecoderInterface, AudioPacket, DecodedAudioFrame, AudioCodec } from '@mimir/cutter/frameProvider/demuxAndDecode/demuxAndDecodeFrameProvider';
import type { AudioFrameV2 } from '@mimir/cutter/frameProvider/frameProvider';
import type { BufferCache, RefCountedBuffer } from '@mimir/cutter/frameProvider/refCountedBuffer';

export interface WebAudioDecoderParameters {
  codec: AudioCodec,
  logger: Logger,
  sampleRate: number,
  channelCount: number,
  bufferCache: BufferCache,
}

export function createWebAudioDecoder(parameters: WebAudioDecoderParameters): AudioDecoderInterface {
  return createWebAudioDecoderWithRecreation(parameters);
}

// ---

function rawAudioFormatFromAudioData(audioData: AudioData): RawAudioFormat {
  switch (audioData.format) {
    case 'f32-planar': return 'float32';
    case 'f32': return 'float32';
    case 's16': return 'pcm16';
    default:
      throw new Error(`Unsupported audio format: ${audioData.format}`);
  }
}

function audioFrameFromAudioData(
  rawAudioInfo: RawAudioInfo,
  sampleOffset: number,
  sampleCount: number,
  audioData: AudioData,
  bufferCache: BufferCache,
  workingBuffers: WorkingBuffers,
): AudioFrameV2 {
  workingBuffers.reset();
  switch (audioData.format) {
    case 'f32':
    case 's16':
    case 'f32-planar': {
      const bufferSize = calculateRawAudioFrameBufferSize(rawAudioInfo, sampleCount);
      const buffer : RefCountedBuffer =
        bufferCache.allocateBuffer(
          bufferSize,
          (buffer_: Uint8Array) => {
            if (audioData.format === 'f32-planar') {
              const channelBuffers : Float32Array[] = [];
              for (let planeIndex = 0; planeIndex < rawAudioInfo.channelCount; planeIndex++) {
                const channelBuffer = workingBuffers.getFloat32Array(sampleCount);
                audioData.copyTo(channelBuffer, { planeIndex });
                channelBuffers.push(channelBuffer);
              }
              const targetSamples = new Float32Array(buffer_.buffer);
              let targetOffset = 0;
              for (let i = 0; i < sampleCount; i++) {
                for (let j = 0; j < channelBuffers.length; j++) {
                  targetSamples[targetOffset] = channelBuffers[j][i];
                  targetOffset += 1;
                }
              }
            } else {
              audioData.copyTo(buffer_, { planeIndex: 0 });
            }
          }
        );


      return {
        ...rawAudioInfo,
        format: rawAudioFormatFromAudioData(audioData),
        sampleOffset,
        sampleCount,
        buffer,
      };
    }
    default:
      throw new Error(`Unsupported audio format: ${audioData.format}`);
  }
}

const MAX_DECODE_QUEUE_SIZE : number = 5;

function audioCodecToCodecString(audioCodec: AudioCodec): string {
  switch (audioCodec) {
    case 'aac': return 'mp4a.40.2';
    default: throw new Error(`Unsupported audio codec: ${audioCodec}`);
  }
}

class WorkingBuffers {
  private float32Count: number = 0;
  private float32Buffers: Float32Array[] = [];
  constructor() { }

  reset() {
    this.float32Count = 0;
  }

  getFloat32Array(count: number): Float32Array {
    if (this.float32Buffers.length === this.float32Count) {
      console.warn(`Allocating new buffer of size: ${count}`);
      const array = new Float32Array(count);
      this.float32Buffers.push(array);
    }
    if (this.float32Buffers[this.float32Count].length < count) {
      console.warn(`Allocating new buffer of size: ${count}`);
      this.float32Buffers[this.float32Count] = new Float32Array(count);
    }
    const result = this.float32Buffers[this.float32Count];
    this.float32Count++;
    return result;
  }
}

/**
 *  NOTE: We need to wrap the actual WebAudioDecoder implementation in this
 *  wrapper which recreates the entire thing when reset() is called. According
 *  to the specs a call to reset() on the AudioDecoder object should reset all
 *  state, drain all queues and cancel all pending callbacks. However, both in
 *  Firefox and Chrome I see that pending callback are not cleared so I still
 *  get callback from the previous run. Because of this put recreate the
 *  AudioDecoder every time we seek.
 */
function createWebAudioDecoderWithRecreation(parameters: WebAudioDecoderParameters): AudioDecoderInterface {
  let inner : AudioDecoderInterface |null = null;

  return {
    close(): void {
      if (inner !== null) {
        inner.close();
        inner = null;
      }
    },
    async push(packet: AudioPacket, abortSignal: AbortSignal): Promise<void> {
      if (inner === null) {
        inner = createWebAudioDecoderInternal(parameters);
      }
      await inner.push(packet, abortSignal);
    },
    async poll(abortSignal: AbortSignal): Promise<DecodedAudioFrame | null> {
      if (inner !== null) {
        return await inner.poll(abortSignal);
      } else {
        return null;
      }
    },
    async flush(abortSignal: AbortSignal): Promise<void> {
      if (inner !== null) {
        return inner.flush(abortSignal);
      }
    },
    reset(): void {
      if (inner !== null) {
        inner.close();
        inner = null;
      }
    }
  };
}

function createWebAudioDecoderInternal(parameters: WebAudioDecoderParameters): AudioDecoderInterface {
  const { channelCount, codec, logger, sampleRate, bufferCache } = parameters;

  const outputAudioFrames : DecodedAudioFrame[] = [];

  const dequeue = new Guard();

  let inputTimestampRange : { start: number, end: number } | null = null;

  const workingBuffers = new WorkingBuffers();

  let audioDecoder : AudioDecoder | null =
    new AudioDecoder({
      error: (error) => {
        logger.error(`WebAudioDecoder: error=${exceptionToString(error)}`);
      },
      output: (audioData: AudioData) => {
        if (audioDecoder === null) {
          // AudioDecoder is not properly implemented by Firefox and Chrome and we'll
          // get pending callback event after the audio decoder is closed.
          return;
        }
        const timestamp : Fraction = frac(audioData.timestamp, 1000000);

        const duration : Fraction = frac(audioData.duration, 1000000);

        const rawAudioInfo : RawAudioInfo = {
          sampleRate: audioData.sampleRate,
          channelCount: audioData.numberOfChannels,
          format: rawAudioFormatFromAudioData(audioData),
        };

        const sampleRateAsFraction = frac(audioData.sampleRate, 1);

        const sampleCountAsFraction =
          multiplyFractions(duration, sampleRateAsFraction);

        const sampleCount = Math.round(approximateFractionValue(sampleCountAsFraction));

        const sampleOffsetAsFraction =
          multiplyFractions(timestamp, sampleRateAsFraction);

        const sampleOffset = Math.round(approximateFractionValue(sampleOffsetAsFraction));

        if ((codec === 'aac') && (sampleCount !== 1024)) {
          console.warn(`Ignoring samples from first packet since these are normally garbage. sampleCount=${sampleCount}`);
          return;
        }

        if (inputTimestampRange && ((audioData.timestamp < inputTimestampRange.start) || (audioData.timestamp > inputTimestampRange.end))) {
          logger.warn(`WebAudioDecoder: unexpected timestamp on payload ${audioData.timestamp}. Ignoring decoded AudioData (sampleOffset=${sampleOffset}, sampleCount=${sampleCount}). Input time stamp range: ${JSON.stringify(inputTimestampRange)}`);
          return;
        }

        const audioBufferSize =
          calculateRawAudioFrameBufferSize(rawAudioInfo, sampleCount);

        const buffer = new Uint8Array(audioBufferSize);
        audioData.copyTo(buffer, { planeIndex: 0 });

        outputAudioFrames.push({
          audioFrame: audioFrameFromAudioData(
            rawAudioInfo,
            sampleOffset,
            sampleCount,
            audioData,
            bufferCache,
            workingBuffers,
          )
        });

        audioData.close();
      }
    });

  audioDecoder.ondequeue = () => { dequeue.notify(); };

  let isConfigured = false;

  return {
    reset(): void {
      if (audioDecoder === null) {
        throw new Error('WebAudioDecoder is closed');
      }
      // At some point if AudioDecoder.reset is fixed we can get rid of the
      // Wrapper and call AudioDecoder.reset here:
      // audioDecoder.reset();
      // isConfigured = false;
      // inputTimestampRange = null;
      throw new Error('Not implemented');
    },
    close(): void {
      outputAudioFrames.splice(0).forEach((a) => a.audioFrame.buffer.close());
      if (audioDecoder === null) {
        throw new Error('WebAudioDecoder already closed');
      }
      audioDecoder.close();
      audioDecoder = null;
    },
    async push(packet: AudioPacket, abortSignal: AbortSignal): Promise<void> {
      if (audioDecoder === null) {
        throw new Error('WebAudioDecoder is closed');
      }
      if (!isConfigured) {
        audioDecoder.configure({
          codec: audioCodecToCodecString(codec),
          numberOfChannels: channelCount,
          sampleRate: sampleRate,
        });
        isConfigured = true;
      }
      while (audioDecoder.decodeQueueSize >= MAX_DECODE_QUEUE_SIZE) {
        await abortify(abortSignal, () => dequeue.wait());
      }

      // AudioDecoder may have been closed while waiting
      if (audioDecoder === null) {
        return;
      }

      const timestampAsMicroSeconds = Math.round(approximateFractionValue(multiplyFractionBy(packet.pts, 1000000)));
      const durationAsMicrosSeconds = Math.round(approximateFractionValue(multiplyFractionBy(packet.duration, 1000000)));

      if (inputTimestampRange === null) {
        inputTimestampRange = { start: timestampAsMicroSeconds, end: timestampAsMicroSeconds };
      } else {
        inputTimestampRange.start = Math.min(inputTimestampRange.start, timestampAsMicroSeconds);
        // We need to add the duration here because chrome adjusts the timestamps when going through the
        // decoder so that the output timestamp matches with the packet alignement.
        inputTimestampRange.end = Math.max(inputTimestampRange.end, timestampAsMicroSeconds + durationAsMicrosSeconds);
      }

      const chunk =
        new EncodedAudioChunk({
          data: packet.payload,
          timestamp: timestampAsMicroSeconds,
          type: 'key',
        });

      audioDecoder.decode(chunk);
    },
    async poll(): Promise<DecodedAudioFrame | null> {
      const [outputAudioFrame] = outputAudioFrames.splice(0, 1);
      if (outputAudioFrame === undefined) {
        return null;
      }
      return outputAudioFrame;
    },
    async flush(abortSignal: AbortSignal): Promise<void> {
      if (audioDecoder === null) {
        throw new Error('WebAudioDecoder is closed');
      }
      if (!isConfigured) {
        return;
      }
      {
        const decoderToFlush = audioDecoder;
        await abortify(abortSignal, () => decoderToFlush.flush());
      }

      if (audioDecoder === null) {
        return;
      }

      while (audioDecoder.decodeQueueSize >= MAX_DECODE_QUEUE_SIZE) {
        await abortify(abortSignal, () => dequeue.wait());
      }
    }
  };
}
